import {
  isFreshNavigation,
  prefersReducedMotion,
  random,
  replaceChildren,
  round,
  shuffle,
  trail,
} from "../utils";

/**
 * A custom element for rendering and animating the CWGW symbol
 */
export default class Nameplate extends HTMLElement {
  /**
   * A collection for cleanup functions, e.g. removing event listeners.
   * @type {Set}
   */
  cleanup = new Set();

  /**
   * Defines the element in the document's custom element registry
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "cwgw-nameplate") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * Connected callback
   */
  async connectedCallback() {
    // Do nothing if the element isn't connected
    if (!this.isConnected) {
      return;
    }

    // Warn and abort if we don't have the expected markup
    if (
      this.childElementCount !== 1 ||
      this.firstElementChild.tagName.toUpperCase() !== "SVG"
    ) {
      const tag = this.tagName.toLowerCase();
      console.warn(
        `<${tag}> was instantiated incorrectly. The component expects a single SVG child element.`,
      );
      return;
    }

    // On new visits or reloads, animate the paths
    if (isFreshNavigation()) {
      await this.enter();
    }

    // Mark the component as set up
    this.dataset.entered = true;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        await this.enter();
        this.dataset.entered = true;
      } else {
        await this.exit();
        delete this.dataset.entered;
      }
    };

    window.addEventListener("visibilitychange", handleVisibilityChange);
    this.cleanup.add(() =>
      window.removeEventListener("visibilitychange", handleVisibilityChange),
    );
  }

  /**
   * Disconnected callback
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Perform "enter" animations
   * @returns {Promise<void>}
   */
  async enter() {
    // Get all child paths and shuffle their order and fill color
    const paths = shuffle(Array.from(this.querySelectorAll("svg > path")));
    const fills = shuffle(paths.map((el) => el.getAttribute("fill")));
    paths.forEach((el, i) => el.setAttribute("fill", fills[i] || ""));
    replaceChildren(this.firstElementChild, ...paths);

    // Animate paths in
    await trail(paths, {
      keyframes: () => {
        const y = round(random(-0.25, 0.25), 2);
        const x = 0 - round(Math.abs(y) ** 1.25, 2);
        return prefersReducedMotion()
          ? [{ opacity: 0 }, { opacity: 1 }]
          : [
              { opacity: 0, translate: `${x}rem ${y}rem` },
              { opacity: 1, translate: "0 0" },
            ];
      },
    });
  }

  /**
   * Perform "exit" animations
   * @returns {Promise<void>}
   */
  async exit() {
    // Animate paths out
    await trail(this.querySelectorAll("svg > path"), {
      duration: 10,
      keyframes: [{ opacity: 1 }, { opacity: 0 }],
    });
  }
}
